'use client';

import { ChevronRight, type LucideIcon } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTriggerClose,
} from '@/components/ui/sidebar';
import * as React from 'react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';

export function NavMain({
  items,
}: {
  items: Array<{
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    collapsible?: boolean; // new flag
    items?: Array<{
      title: string;
      url: string;
    }>;
  }>;
}) {
  return (
    <SidebarGroup className={'bg-background flex h-full flex-col justify-between'}>
      <SidebarHeader>
        <div className={'flex justify-between'}>
          <div className={'translate-1'}>
            <ThemeSwitch />
          </div>
          <SidebarTriggerClose className="flex size-12 items-center self-center lg:hidden" />
        </div>
      </SidebarHeader>
      <SidebarMenu>
        {items.map((item) => {
          const canCollapse =
            item.collapsible !== false && item.items && item.items.length > 0;

          if (!canCollapse) {
            // simple link
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton className={'py-6'} asChild tooltip={item.title}>
                  <a href={item.url} className={item.isActive ? 'font-semibold' : undefined}>
                    {/* {item.icon && <item.icon />}*/}
                    <span className={'font-sans text-xl font-medium tracking-tight uppercase'}>
                      {item.title}
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }

          // collapsible section
          return (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={item.isActive}
              className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton className={'py-6'} tooltip={item.title}>
                    {/*{item.icon && <item.icon />}*/}
                    <span className={'font-mono text-4xl font-medium tracking-tight'}>
                      {item.title}
                    </span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub className={'space-y-1 py-3'}>
                    {item.items!.map((sub) => (
                      <SidebarMenuSubItem key={sub.title}>
                        <SidebarMenuSubButton asChild>
                          <a href={sub.url}>
                            <span className={'font-mono text-lg font-normal tracking-tight'}>
                              {sub.title}
                            </span>
                          </a>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
      <SidebarGroupLabel className={'invisible'}>Ucon Fixings</SidebarGroupLabel>
    </SidebarGroup>
  );
}

function ThemeSwitch() {
  const [mounted, setMounted] = React.useState(false);
  const [showButton, setShowButton] = React.useState(false);
  const { setTheme, resolvedTheme } = useTheme();

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (mounted) {
      setShowButton(false);
      requestAnimationFrame(() => setShowButton(true));
    }
  }, [mounted, resolvedTheme]);

  if (!mounted) {
    const isDark = resolvedTheme === 'dark';

    return (
      <Button
        variant="outline"
        aria-label="Toggle theme"
        className={`bg-primary-foreground dark:bg-primary-foreground hover:bg-secondary dark:hover:bg-secondary
          inline-flex size-10 transform items-center justify-center self-start rounded-md transition-all
          duration-300 ease-in-out`}>
        {isDark ? (
          <span
            className={`${showButton ? 'scale-100 opacity-100 transition-all duration-300 ease-in-out' : 'scale-50 opacity-0 transition-all duration-300 ease-in-out'}`}>
            {''}
          </span>
        ) : (
          <span
            className={`${showButton ? 'scale-100 opacity-100 transition-all duration-300 ease-in-out' : 'scale-50 opacity-0 transition-all duration-300 ease-in-out'}`}>
            {''}
          </span>
        )}
      </Button>
    );
  }

  const isDark = resolvedTheme === 'dark';

  const handleToggle = () => {
    setTheme(isDark ? 'light' : 'dark');
    navigator.vibrate?.(50);
  };

  return (
    <Button
      variant="outline"
      onClick={handleToggle}
      aria-label="Toggle theme"
      className={`bg-primary-foreground dark:bg-primary-foreground hover:bg-secondary dark:hover:bg-secondary
        inline-flex size-10 transform items-center justify-center self-start rounded-md transition-all
        duration-300 ease-in-out`}>
      {isDark ? (
        <span
          className={`${showButton ? 'scale-100 opacity-100 transition-all duration-300 ease-in-out' : 'scale-50 opacity-0 transition-all duration-300 ease-in-out'}`}>
          {'🌙'}
        </span>
      ) : (
        <span
          className={`${showButton ? 'scale-100 opacity-100 transition-all duration-300 ease-in-out' : 'scale-50 opacity-0 transition-all duration-300 ease-in-out'}`}>
          {'☀️'}
        </span>
      )}
    </Button>
  );
}
